/* eslint-disable no-restricted-globals */

import { getMeristemContext, getRouteId } from "src/utils/meristemContext";
import getStore from "src/utils/redux/store";
import { captureException } from "src/utils/error";
import {
  isEligibleForMhtPostCheckoutBuyflow,
  isFreemiumUpsellDuringOnboarding,
  isInAppNativePurchaseEligible,
  isITunesPlansEligible,
  isSelectedPlanEligibleForMhtPostCheckoutBuyflow,
  isStateEligibleForMhtPostCheckoutBuyflow,
  shouldRedirectToPostCheckoutContext,
  userHasPurchasedNoomClinical,
} from "src/utils/userSegment/features";
import goto from ".";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  pageSetPushReroll,
  pushReroll,
} from "./utils";
import {
  legacyPurchaseRoot,
  setRecommendedPlanById,
  hrtPaymentRoot,
} from "../../utils/redux/slices/recommendedPlan";
import {
  getAndroidMessageHandlers,
  getIOSMessageHandlers,
  IOSMessageHandlers,
  NativePurchaseInfo,
} from "src/utils/in-app/messageHandlers";
import { isB2BUpsellClinicalRoute, isHM } from "src/utils/userSegment";
import { getTelehealthPlanId } from "@utils/api/telehealth";
import { getTierByPlanId } from "@utils/plans";
import { disableGlobalExit } from "src/utils/globalExit";
import { GotoEnrollmentParams } from "./enrollment";
import { getPromoCodeState } from "src/utils/redux/slices/promoCode";
import { getFeatureFlagValue } from "../featureFlags";
import { updateForwardedParams } from "src/utils/urlParams";
import { updateSessionState } from "src/pageDefinitions/session";
import { setNoomSessionStorage } from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";

// "/purchase/:planId"
// "/purchase/(en|es|de)/:planId"
// "/purchase-hm/:planId"
function isPurchase() {
  return ["payment", "payment-survey", "payment-checkout"].includes(
    getMeristemContext().context_type
  );
}

export default {
  async purchaseSurvey(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    goto.layer("payment-survey");
  },

  async chooseTrialPrice(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    pageSetPushReroll("/payment/chooseTrialPrice");
  },

  payment() {
    pushReroll(`${legacyPurchaseRoot()}#/payment`, isPurchase);
  },

  async checkout(planId?: string, forceReroll = false) {
    if (isB2BUpsellClinicalRoute()) {
      goto.layer("payment-checkout");
      return;
    }

    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    if (isHM()) {
      pageSetPushReroll(`/payment/hm/checkout`);
      return;
    }

    pushReroll(
      `${legacyPurchaseRoot()}#/checkout`,
      () => isPurchase() && !forceReroll
    );
  },

  async hrtCheckout(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    pushReroll(hrtPaymentRoot(), isPurchase);
  },

  async telehealthPaymentDetails() {
    const telehealthPlanId = getTelehealthPlanId();
    await getStore().dispatch(
      setRecommendedPlanById(telehealthPlanId, {
        overwriteForced: true,
      })
    );

    goto.checkout();
  },

  nativePurchase() {
    if (isInsideIOSWebKit()) {
      try {
        // NOTE(norbert): the buyflowPurchaseNoDetails endpoint was only introduced
        // in the iOS 9.14 release, so we need to check if it exists before calling it
        const messageHandlers = getIOSMessageHandlers();
        if (
          isInAppNativePurchaseEligible() &&
          messageHandlers.buyflowPurchaseNoDetails
        ) {
          // If app and route support web purchase, use that
          goto.layer("payment-results");
        } else {
          messageHandlers.buyflowPurchase.postMessage({});
        }
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.ios");
      }
    }

    if (isInsideAndroidWebView()) {
      try {
        const messageHandlers = getAndroidMessageHandlers();
        messageHandlers.buyflowPurchase();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchase: no message handler found");

    return false;
  },

  nativePurchaseNoDetails() {
    const state = getStore().getState();
    const { recommendedPlan, userData } = state;
    let sendPurchaseToNative: () => void | undefined;
    let productId: string;

    if (isInsideIOSWebKit()) {
      const messageHandlers = getIOSMessageHandlers();
      let messageHandler:
        | IOSMessageHandlers["buyflowPurchaseProduct"]
        | IOSMessageHandlers["buyflowPurchaseNoDetails"];

      if (isITunesPlansEligible()) {
        messageHandler =
          messageHandlers?.buyflowPurchaseProduct ||
          messageHandlers?.buyflowPurchaseNoDetails; // buyflowPurchaseProduct added in app version 10.9
      } else {
        messageHandler = messageHandlers?.buyflowPurchaseNoDetails;
      }

      productId = recommendedPlan.itunes_product_id || "";
      const productInfoPayload: NativePurchaseInfo = {
        productData: {
          productId,
          subscriptionLength: recommendedPlan.billing_cycle_duration,
        },
        programAvailability: {
          curriculum: "HW",
          isAvailable: true,
          language: userData.language,
        },
        tracking: {
          routeId: getRouteId(),
          tier: getTierByPlanId(recommendedPlan.braintree_id),
        },
      };
      sendPurchaseToNative =
        messageHandler &&
        (() => messageHandler.postMessage(productInfoPayload));
    } else if (isInsideAndroidWebView()) {
      const messageHandlers = getAndroidMessageHandlers();
      productId = recommendedPlan.android_product_id || "";
      const productInfoPayload: NativePurchaseInfo = {
        productData: {
          productId,
          subscriptionLength: recommendedPlan.billing_cycle_duration,
        },
        programAvailability: {
          curriculum: "HW",
          isAvailable: true,
          language: userData.language,
        },
        tracking: {
          routeId: getRouteId(),
          tier: getTierByPlanId(recommendedPlan.braintree_id),
        },
      };
      sendPurchaseToNative =
        messageHandlers.buyflowPurchaseProduct &&
        (() =>
          messageHandlers.buyflowPurchaseProduct(
            JSON.stringify(productInfoPayload)
          ));
    }

    const isProductIdRequired = isITunesPlansEligible();
    if (isProductIdRequired && !productId) {
      captureException(
        new Error("No productId set for native purchase"),
        "nativePurchaseNoDetails"
      );
    }

    if (isInAppNativePurchaseEligible() && sendPurchaseToNative) {
      try {
        sendPurchaseToNative();
        return true;
      } catch (err) {
        captureException(err, "nativePurchaseNoDetails");
      }
    } else {
      captureException(
        new Error("Native bridge function not found"),
        "nativePurchaseNoDetails"
      );
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseNoDetails: could not call message handler");

    return false;
  },

  nativePurchaseComplete() {
    if (isInsideIOSWebKit()) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseCompleted.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      try {
        const messageHandlers = getAndroidMessageHandlers();
        messageHandlers.buyflowPurchaseCompleted();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseComplete: no message handler found");
    return false;
  },

  redirectToPostEnrollment({
    planId,
    paymentMethod,
    name,
    email,
  }: {
    planId?: string;
    paymentMethod?: string;
    name?: string;
    email?: string;
  }) {
    const state = getStore().getState();
    const { checkout } = state;
    const promoCodeState = getPromoCodeState(state);
    const enrollmentParams: GotoEnrollmentParams = {
      planId,
      paymentMethod,
      name,
      email,
    };

    const { promoCodeIsVip } = promoCodeState;
    if (promoCodeIsVip != null && promoCodeIsVip) {
      enrollmentParams.piv = promoCodeIsVip;
    }

    if (checkout.multiUserPlan.toggled) {
      if (getFeatureFlagValue("useBaselinePriceForMultiUserPlan")) {
        updateForwardedParams({ experimentTag: "exr143var1" });
      } else {
        updateForwardedParams({ experimentTag: "exr105var1" });
      }
    }
    updateForwardedParams({ noom_plan_id: null });

    // Persist some params used in goto.navigateFromCheckout below so that the post-checkout pages
    // can use the same function to navigate
    updateSessionState("browser", {
      enrollmentForwardParams: enrollmentParams,
    });

    if (userHasPurchasedNoomClinical()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.telehealthEnrollmentSetup();
      return;
    }

    if (
      isEligibleForMhtPostCheckoutBuyflow() &&
      isStateEligibleForMhtPostCheckoutBuyflow() &&
      isSelectedPlanEligibleForMhtPostCheckoutBuyflow()
    ) {
      goto.hrtSurvey();
      return;
    }

    if (shouldRedirectToPostCheckoutContext()) {
      goto.layer("payment-post-checkout");
    } else {
      goto.navigateFromCheckout();
    }
  },

  // Handle post-checkout navigation.
  navigateFromCheckout() {
    // Disable global exit button after successful checkout for freemium upsell during onboarding
    if (isFreemiumUpsellDuringOnboarding()) {
      disableGlobalExit();
    }

    // If the user is ineligible for noom premium, they'll end up
    // in a failover pageset that will continue to the next eligible context after noom premium
    goto.noomPremium();
  },
};
